import { z } from "zod/v4-mini";

import { cz } from "@/utils/zodUtils.ts";

const booleanSchema = z.union([z.stringbool(), z.boolean()]);

// PeriodType schema
const periodTypeSchema = z.union([
  z.literal("fasting"),
  z.literal("before_meal"),
  z.literal("after_meal"),
  z.literal("all"),
]);

const alertCategorySchema = z.literal([
  "severe_hypoglycemia_declared",
  "hyperglycemia",
  "hypoglycemia",
  "time_above_range",
  "time_below_range",
  "time_in_range",
  "observance_transmission",
  "observance_measures",
]);

export type CreateAlertConfig = {
  type: AlertType;
  category: AlertCategory;
};

const hypoglycemiaAlertParametersBGMSchema = cz.camelCaseObject({
  max_number_of_events: z.number(),
  severe_only: booleanSchema,
  periods: periodTypeSchema,
  number_of_days: z.number(),
});

const hypoglycemiaAlertParametersCGMSchema = cz.camelCaseObject({
  max_number_of_events: z.number(),
  severe_only: booleanSchema,
  number_of_days: z.number(),
});

const hyperglycemiaAlertParametersBGMSchema = cz.camelCaseObject({
  max_number_of_events: z.number(),
  severe_only: booleanSchema,
  periods: periodTypeSchema,
  number_of_days: z.number(),
});

const hyperglycemiaAlertParametersCGMSchema = cz.camelCaseObject({
  max_number_of_events: z.number(),
  severe_only: booleanSchema,
  number_of_days: z.number(),
});

const timeAboveRangeCGMSchema = cz.camelCaseObject({
  duration_proportion: z.number(),
  severe_only: booleanSchema,
  number_of_days: z.number(),
});

const timeBelowRangeCGMSchema = cz.camelCaseObject({
  duration_proportion: z.number(),
  severe_only: booleanSchema,
  number_of_days: z.number(),
});

const timeInRangeCGMSchema = cz.camelCaseObject({
  duration_proportion: z.number(),
  number_of_days: z.number(),
});

const severeHypoglycemiaDeclaredParametersSchema = cz.camelCaseObject({
  number_of_days: z.number(),
});

const transmissionAlertParametersSchema = cz.camelCaseObject({
  number_of_days: z.number(),
});

const measurementAlertParametersSchema = cz.camelCaseObject({
  number_of_days: z.number(),
  min_number_of_measures_per_day: z.number(),
});

const alertConfigParametersSchema = z.union([
  z.object({
    type: z.literal("bgm"),
    category: z.literal("hypoglycemia"),
    parameters: hypoglycemiaAlertParametersBGMSchema,
  }),
  z.object({
    type: z.literal("bgm"),
    category: z.literal("hyperglycemia"),
    parameters: hyperglycemiaAlertParametersBGMSchema,
  }),
  z.object({
    type: z.literal("cgm"),
    category: z.literal("hypoglycemia"),
    parameters: hypoglycemiaAlertParametersCGMSchema,
  }),
  z.object({
    type: z.literal("cgm"),
    category: z.literal("hyperglycemia"),
    parameters: hyperglycemiaAlertParametersCGMSchema,
  }),
  z.object({
    type: z.literal("cgm"),
    category: z.literal("time_above_range"),
    parameters: timeAboveRangeCGMSchema,
  }),
  z.object({
    type: z.literal("cgm"),
    category: z.literal("time_below_range"),
    parameters: timeBelowRangeCGMSchema,
  }),
  z.object({
    type: z.literal("other"),
    category: z.literal("observance_measures"),
    parameters: measurementAlertParametersSchema,
  }),
  z.object({
    type: z.literal("cgm"),
    category: z.literal("time_in_range"),
    parameters: timeInRangeCGMSchema,
  }),
  z.object({
    type: z.literal("other"),
    category: z.literal("observance_transmission"),
    parameters: transmissionAlertParametersSchema,
  }),
  z.object({
    type: z.literal("other"),
    category: z.literal("severe_hypoglycemia_declared"),
    parameters: severeHypoglycemiaDeclaredParametersSchema,
  }),
]);

// AlertConfig schema
export const alertConfigSchema = z.intersection(
  cz.camelCaseObject({
    id: z.string(),
    can_delete: z.boolean(),
  }),
  alertConfigParametersSchema,
);

export const alertSummarySchema = cz.camelCaseObject({
  id: cz.id(),
  patient_id: cz.id(),
  category: alertCategorySchema,
  can_resolve: z.boolean(),
});

export const alertTypeSchema = z.literal(["bgm", "cgm", "other"]);

// Export the types as well
export type AlertBGMPeriod = z.infer<typeof periodTypeSchema>;
export type AlertCategory = z.infer<typeof alertCategorySchema>;
export type AlertConfig = z.infer<typeof alertConfigSchema>;
export type AlertType = z.infer<typeof alertTypeSchema>;
export type EditAlertConfig = {
  id: string;
  parameters: unknown; // TODO STE-1607 type as a discriminated union over alert kind
};
export type AlertSummary = z.infer<typeof alertSummarySchema>;

export type AlertParameters = z.infer<typeof alertConfigParametersSchema>;

export type HypoglycemiaAlertParametersBGM = z.infer<typeof hypoglycemiaAlertParametersBGMSchema>;
export type HypoglycemiaAlertParametersCGM = z.infer<typeof hypoglycemiaAlertParametersCGMSchema>;
export type HyperglycemiaAlertParametersBGM = z.infer<typeof hyperglycemiaAlertParametersBGMSchema>;
export type HyperglycemiaAlertParametersCGM = z.infer<typeof hyperglycemiaAlertParametersCGMSchema>;
export type TimeAboveRangeCGM = z.infer<typeof timeAboveRangeCGMSchema>;
export type TimeBelowRangeCGM = z.infer<typeof timeBelowRangeCGMSchema>;
export type TimeInRangeCGM = z.infer<typeof timeInRangeCGMSchema>;
export type TransmissionAlertParameters = z.infer<typeof transmissionAlertParametersSchema>;
export type MeasurementAlertParameters = z.infer<typeof measurementAlertParametersSchema>;

export const alertCategoriesTranslations = {
  hypoglycemia: "alertConfig.hypoglycemiaEvent",
  time_below_range: "alertConfig.hypoglycemiaEventTbr",
  hyperglycemia: "alertConfig.hyperglycemiaEvent",
  time_above_range: "alertConfig.hyperglycemiaEventTar",
  time_in_range: "alertConfig.timeInRange",
  observance_transmission: "alertConfig.transmission",
  observance_measures: "alertConfig.measurement",
  severe_hypoglycemia_declared: "alertConfig.perceivedSevereHypoglycemia",
} as const satisfies Record<AlertCategory, string>;
